import React, { useState } from 'react';
import { Download, Shield, Loader2, X, FileText, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { exportMyData } from '../utils/accountSettingsApi';

const font = "'Inria Serif', serif";

// Plain-English category labels shown in the confirmation dialog. Keeps users
// informed about exactly what they're about to receive — strengthens the GDPR
// transparency story and reassures users that nothing sensitive (passwords,
// OTP codes) ships in the export.
const EXPORT_CATEGORIES = [
  { label: 'Profile', detail: 'Name, email, phone, photo, preferences' },
  { label: 'Guest passes & QR history', detail: 'Every venue visit you unlocked' },
  { label: 'Transactions', detail: 'Payments, tips, refunds (last 10 years)' },
  { label: 'Invites', detail: 'Sent & received — guest, host and staff' },
  { label: 'Messages & conversations', detail: 'In-app chats you participated in' },
  { label: 'Reviews & reports', detail: 'Anything you wrote about a venue' },
  { label: 'Support requests', detail: 'Threads and messages with our team' },
  { label: 'Notifications', detail: 'In-app and push history' },
  { label: 'Devices & sessions', detail: 'Trusted devices, push subscriptions' },
  { label: 'Host / venue records', detail: 'Applications, payouts, ledger (if applicable)' },
];

/**
 * GDPR Art. 15 / Art. 20 — Reusable "Download my data" section.
 *
 * Renders an info card explaining what the export contains, plus a primary
 * button that opens a confirmation dialog listing the categories about to be
 * downloaded. Confirming streams a JSON file from `/api/account/export-data`
 * and triggers a browser save dialog.
 */
export const DownloadMyDataSection = ({ dataTestIdPrefix = 'account' }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const openDialog = () => {
    if (loading) return;
    setOpen(true);
  };

  const closeDialog = () => {
    if (loading) return;
    setOpen(false);
  };

  const handleConfirm = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { filename, size } = await exportMyData();
      const kb = Math.max(1, Math.round(size / 1024));
      toast.success(`Downloaded ${filename} (${kb} KB)`);
      setOpen(false);
    } catch (err) {
      toast.error(err.message || 'Could not prepare your data export.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        data-testid={`${dataTestIdPrefix}-download-data-section`}
        style={{
          marginTop: 24,
          padding: 16,
          borderRadius: 14,
          border: '1px solid var(--card-border)',
          background: 'var(--status-info-surface)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Shield size={20} color="var(--status-info-text)" />
          <p style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--status-info-text)', margin: 0 }}>
            Download my data
          </p>
        </div>
        <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: '0 0 12px', lineHeight: 1.5 }}>
          Get a copy of every record Allure holds about you — profile, passes,
          transactions, messages, reviews, notifications and more. Delivered as a
          single <strong>JSON file</strong>. Sensitive auth data (passwords, OTP
          codes) is never included.
        </p>
        <button
          type="button"
          onClick={openDialog}
          data-testid={`${dataTestIdPrefix}-download-data-button`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 16px',
            border: '1px solid var(--status-info-text)',
            background: 'var(--surface)',
            color: 'var(--status-info-text)',
            fontFamily: font,
            fontSize: 14,
            fontWeight: 700,
            borderRadius: 10,
            cursor: 'pointer',
          }}
        >
          <Download size={16} />
          Download my data
        </button>
      </div>

      {open && (
        <div
          data-testid={`${dataTestIdPrefix}-download-data-modal`}
          onClick={closeDialog}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 460,
              maxHeight: '90vh',
              overflowY: 'auto',
              background: 'var(--surface)',
              borderRadius: 16,
              padding: 22,
              boxShadow: 'var(--premium-shadow-main)',
              fontFamily: font,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileText size={22} color="var(--status-info-text)" />
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>
                  Your data export
                </h3>
              </div>
              <button
                type="button"
                onClick={closeDialog}
                disabled={loading}
                data-testid={`${dataTestIdPrefix}-download-data-modal-close`}
                style={{ background: 'none', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', padding: 4, color: 'var(--text)' }}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.55, margin: '0 0 14px' }}>
              We&rsquo;ll prepare a <strong>single JSON file</strong> with the following categories:
            </p>

            <ul
              data-testid={`${dataTestIdPrefix}-download-data-modal-categories`}
              style={{ listStyle: 'none', padding: 0, margin: '0 0 16px' }}
            >
              {EXPORT_CATEGORIES.map((cat) => (
                <li
                  key={cat.label}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '8px 0',
                    borderBottom: '1px solid var(--hairline)',
                  }}
                >
                  <CheckCircle2 size={16} color="var(--status-success-text)" style={{ flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{cat.label}</p>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-3)', lineHeight: 1.4 }}>{cat.detail}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div
              style={{
                background: 'var(--status-info-surface)',
                border: '1px solid var(--card-border)',
                borderRadius: 10,
                padding: '10px 12px',
                marginBottom: 18,
                fontSize: 12,
                color: 'var(--text-2)',
                lineHeight: 1.5,
              }}
            >
              Passwords, OTP secrets and verification codes are <strong>never</strong> included.
              Financial records are kept for up to 10 years for tax and PSD2 compliance.
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={closeDialog}
                disabled={loading}
                data-testid={`${dataTestIdPrefix}-download-data-modal-cancel`}
                style={{
                  flex: 1,
                  padding: '11px 0',
                  border: '1px solid var(--hairline)',
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  fontFamily: font,
                  fontSize: 14,
                  fontWeight: 600,
                  borderRadius: 10,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={loading}
                data-testid={`${dataTestIdPrefix}-download-data-modal-confirm`}
                style={{
                  flex: 1,
                  padding: '11px 0',
                  border: 'none',
                  background: 'var(--status-info-text)',
                  color: '#fff',
                  fontFamily: font,
                  fontSize: 14,
                  fontWeight: 700,
                  borderRadius: 10,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                {loading ? 'Preparing…' : 'Download JSON'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DownloadMyDataSection;
